import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { DELIVERY_COLUMNS } from '../services/deliveries';
import { DEFAULT_DELIVERY_FEE, orderBarColor } from '../constants/deliveryStatus';
import { useToast } from '../components/Toast';
import { SkeletonRow } from '../components/Skeleton';
import ConfirmDialog from '../components/ConfirmDialog';
import { AVATAR_BG, AVATAR_FG } from '../constants/avatar';
import {
  Truck, CheckCircle, XCircle, MapPin, Phone,
  ChevronRight, X, Navigation, RotateCcw, User, Wallet,
  ChevronDown, ChevronUp, CheckCircle2, MessageCircle, AlertTriangle, ArrowLeft,
  Package, Store, Calendar,
} from 'lucide-react';
import { formatOrderTime as formatTime } from '../utils/orderTime';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

function dayLabel(iso) {
  if (!iso) return 'Date inconnue';
  const d = new Date(iso);
  const now = new Date();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === yest.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

function groupByDay(orders, dateKey = 'created_at') {
  const groups = [];
  const map = new Map();
  const sorted = [...orders].sort((a, b) => new Date(b[dateKey] || b.created_at) - new Date(a[dateKey] || a.created_at));
  for (const order of sorted) {
    const ref = order[dateKey] || order.created_at;
    const key = ref ? new Date(ref).toDateString() : 'unknown';
    if (!map.has(key)) {
      const group = { label: dayLabel(ref), key, items: [] };
      map.set(key, group);
      groups.push(group);
    }
    map.get(key).items.push(order);
  }
  return groups;
}

function groupVersementsByCollectionDate(versements, orders) {
  return versements.map(v => ({
    ...v,
    items: orders.filter(o => String(o.expedition_versement_id) === String(v.id)),
  })).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

const statusCfg = {
  pending:   { label: 'En transit', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  transit:   { label: 'En transit', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  delivered: { label: 'Livré',      color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
  failed:    { label: 'Retour',     color: '#be123c', bg: '#fff1f2', border: '#fecdd3' },
};

const OrderCard = ({ order, onClick }) => {
  const s = statusCfg[order.status] || statusCfg.transit;

  return (
    <div
      style={{ background: 'white', borderRadius: '14px', border: '1.5px solid #eef0f6', boxShadow: '0 2px 6px rgba(19,40,72,0.05)', display: 'flex', overflow: 'hidden', cursor: 'pointer' }}
      onClick={() => onClick(order)}
    >
      <div style={{ width: '4px', flexShrink: 0, background: orderBarColor(order) }} />
      <div style={{ flex: 1, minWidth: 0, padding: '11px 12px 11px 14px' }}
        onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>

          {/* Colonne gauche */}
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Numéro + client */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
              {order.order_number && (
                <span style={{ fontSize: '10px', fontWeight: 700, color: '#173b78', fontFamily: 'monospace', background: '#eff6ff', padding: '1px 6px', borderRadius: '5px', flexShrink: 0 }}>
                  #{order.order_number}
                </span>
              )}
              <span style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {order.client || '—'}
              </span>
            </div>

            {/* Produit */}
            {order.product_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#334155', fontWeight: 600, marginBottom: '3px' }}>
                <Package size={10} color="#6366f1" style={{ flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {order.product_name}{order.quantity > 1 ? ` ×${order.quantity}` : ''}
                </span>
              </div>
            )}

            {/* Adresse */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>
              <MapPin size={10} style={{ flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {order.address || '—'}
              </span>
            </div>

            {/* Badges */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '5px', background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
                {s.label}
              </span>
              {order.expedition_versement_id && (
                <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '5px', background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}>
                  Collecté ✓
                </span>
              )}
            </div>

            {/* Motif retour */}
            {order.status === 'failed' && order.return_reason && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '7px', padding: '4px 8px', marginTop: '5px' }}>
                <AlertTriangle size={10} color="#c2410c" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#7c2d12', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {order.return_reason}
                </span>
              </div>
            )}
          </div>

          {/* Colonne droite */}
          <div style={{ flexShrink: 0, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
            {order.amount && (
              <span style={{ fontSize: '13px', fontWeight: 800, color: '#173b78', whiteSpace: 'nowrap' }}>
                {fmt(order.amount)} F
              </span>
            )}
            <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
              {formatTime(order.delivered_at || order.expedition_at || order.created_at)}
            </span>
            <ChevronRight size={14} color="#c7d2e0" />
          </div>
        </div>
      </div>
    </div>
  );
};

const DayGroup = ({ group, onOrderClick }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
      <span style={{ fontSize: '12px', fontWeight: 800, color: '#173b78', textTransform: 'capitalize' }}>{group.label}</span>
      <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>· {group.items.length} commande{group.items.length > 1 ? 's' : ''}</span>
      <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {group.items.map(order => <OrderCard key={order.id} order={order} onClick={onOrderClick} />)}
    </div>
  </div>
);

export default function Expedition({ storeFilter = null, onBack = null }) {
  const toast = useToast();
  const [orders, setOrders]                   = useState([]);
  const [drivers, setDrivers]                 = useState([]);
  const [expeditionVersements, setExpeditionVersements] = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [tab, setTab]                         = useState('transit');
  const [selected, setSelected]               = useState(null);
  const [saving, setSaving]                   = useState(false);
  const [collecting, setCollecting]           = useState(false);
  const [expandedVersement, setExpandedVersement] = useState(null);

  // Motif retour
  const [motifModal, setMotifModal]     = useState(null); // { action: 'failed'|'retour_dakar' }
  const [motifText, setMotifText]       = useState('');

  // Ramener à Dakar
  const [showDakarPicker, setShowDakarPicker] = useState(false);

  // Annuler
  const [confirmCancel, setConfirmCancel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase.from('deliveries').select(DELIVERY_COLUMNS).eq('expedition', true).order('created_at', { ascending: false });
    if (storeFilter) query = query.eq('store', storeFilter);
    const [{ data: d }, { data: dr }, { data: ev }] = await Promise.all([
      query,
      supabase.from('drivers').select('id, name, color, initials, status').order('name'),
      supabase.from('expedition_versements').select('*').order('created_at', { ascending: false }),
    ]);
    if (d)  setOrders(d);
    if (dr) setDrivers(dr);
    if (ev) setExpeditionVersements(ev);
    setLoading(false);
  }, [storeFilter]);

  useEffect(() => {
    load();
    const channel = supabase
      .channel('realtime:expedition-v4')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'deliveries' }, () => load())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [load]);

  useEffect(() => {
    if (!selected) return;
    const fresh = orders.find(o => o.id === selected.id);
    if (!fresh) { setSelected(null); return; }
    if (fresh !== selected) setSelected(fresh);
  }, [orders, selected]);

  useEffect(() => {
    if (!selected) return;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [selected]);

  // Une annulation définitive sort la commande du circuit expédition : elle
  // rejoint les annulations de sa boutique. Sans ce filtre elle restait
  // affichée dans "Retours", donnant l'impression que l'annulation échouait.
  const active           = orders.filter(o => o.confirmation_status !== 'cancelled');
  const transit          = active.filter(o => ['pending', 'transit'].includes(o.status));
  const delivered        = active.filter(o => o.status === 'delivered');
  const failed           = active.filter(o => o.status === 'failed');
  const pendingDelivered = delivered.filter(o => !o.expedition_versement_id);
  const totalToCollect   = pendingDelivered.reduce((s, o) => s + (Number(o.amount) || 0), 0);
  const collectionHistory = groupVersementsByCollectionDate(expeditionVersements, delivered);

  const tabs = [
    { id: 'transit',   label: 'En transit', count: transit.length,   color: '#2563eb' },
    { id: 'delivered', label: 'Livrées',    count: pendingDelivered.length, color: '#15803d' },
    { id: 'failed',    label: 'Retours',    count: failed.length,    color: '#be123c' },
  ];

  // ── Marquer livré / retour ────────────────────────────────────────
  const markStatus = async (id, status, clientName, returnReason) => {
    setSaving(true);
    const updates = status === 'delivered'
      ? { status, delivered_at: new Date().toISOString(), return_reason: null }
      : { status, ...(returnReason ? { return_reason: returnReason } : {}) };
    const { error } = await supabase.from('deliveries').update(updates).eq('id', id);
    if (error) {
      toast(error.message || 'Erreur', 'error');
    } else {
      setOrders(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o));
      toast(
        status === 'delivered' ? `${clientName} — Livraison confirmée` : `${clientName} — Retour enregistré`,
        status === 'delivered' ? 'success' : 'error'
      );
      setSelected(null);
    }
    setSaving(false);
  };

  // ── Réexpédier (retour → transit) ────────────────────────────────
  const reexpedier = async () => {
    if (!selected) return;
    setSaving(true);
    const updates = { status: 'transit', return_reason: null };
    const { error } = await supabase.from('deliveries').update(updates).eq('id', selected.id);
    if (error) {
      toast(error.message || 'Erreur', 'error');
    } else {
      setOrders(prev => prev.map(o => o.id === selected.id ? { ...o, ...updates } : o));
      toast(`${selected.client} — Réexpédition en cours`, 'success');
      setSelected(null);
    }
    setSaving(false);
  };

  // ── Ramener à Dakar ──────────────────────────────────────────────
  const ramenerDakar = async (driverId) => {
    if (!selected) return;
    setSaving(true);

    // Récupère le tarif Dakar de la boutique
    let dakarFee = DEFAULT_DELIVERY_FEE;
    if (selected.store) {
      const { data: prof } = await supabase
        .from('profiles')
        .select('fee_dakar')
        .eq('store_name', selected.store)
        .eq('role', 'merchant')
        .maybeSingle();
      if (prof?.fee_dakar != null) dakarFee = prof.fee_dakar;
    }

    const updates = {
      expedition: false,
      driver_id: driverId || null,
      status: driverId ? 'transit' : 'pending',
      delivery_fee: dakarFee,
      return_reason: null,
    };
    const { error } = await supabase.from('deliveries').update(updates).eq('id', selected.id);
    if (error) {
      toast(error.message || 'Erreur', 'error');
    } else {
      const driver = drivers.find(d => String(d.id) === String(driverId));
      toast(
        driver ? `Réassigné à ${driver.name} — retour dans Commandes` : 'Remis dans Commandes (sans livreur)',
        'success'
      );
      setOrders(prev => prev.filter(o => o.id !== selected.id));
      setSelected(null);
      setShowDakarPicker(false);
    }
    setSaving(false);
  };

  // ── Annuler définitivement ────────────────────────────────────────
  const cancelOrder = async (id) => {
    setSaving(true);
    const { error } = await supabase.from('deliveries').update({ confirmation_status: 'cancelled', driver_id: null }).eq('id', id);
    if (error) {
      toast(error.message || 'Erreur', 'error');
    } else {
      setOrders(prev => prev.filter(o => o.id !== id));
      const boutique = orders.find(o => o.id === id)?.store;
      toast(boutique ? `Commande annulée · visible dans les annulées de ${boutique}` : 'Commande annulée définitivement', 'info');
      setSelected(null);
    }
    setConfirmCancel(null);
    setSaving(false);
  };

  // ── Collecte ─────────────────────────────────────────────────────
  const collecterTout = async () => {
    if (!pendingDelivered.length) return;
    setCollecting(true);
    const { data: versement, error } = await supabase
      .from('expedition_versements')
      .insert({ amount: totalToCollect, nb_deliveries: pendingDelivered.length })
      .select().single();
    if (error || !versement) {
      toast(error?.message || 'Erreur lors de la collecte', 'error');
      setCollecting(false);
      return;
    }
    const { error: updError } = await supabase
      .from('deliveries')
      .update({ expedition_versement_id: versement.id })
      .in('id', pendingDelivered.map(o => o.id));
    if (updError) {
      toast(updError.message || 'Collecte créée mais liaison incomplète', 'error');
    } else {
      toast(`${pendingDelivered.length} livraison${pendingDelivered.length > 1 ? 's' : ''} · ${fmt(totalToCollect)} FCFA collectés`, 'success');
    }
    setCollecting(false);
    await load();
  };

  const closeSheet = () => {
    setSelected(null);
    setShowDakarPicker(false);
    setMotifModal(null);
    setMotifText('');
  };

  const handleRetourClick = () => {
    setMotifText('');
    setMotifModal({ action: 'failed' });
  };

  const handleMotifConfirm = async () => {
    if (!motifModal || !selected) return;
    await markStatus(selected.id, 'failed', selected.client, motifText.trim());
    setMotifModal(null);
    setMotifText('');
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-enter" style={{ maxWidth: '860px', margin: '0 auto' }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '3px' }}>
        {onBack && (
          <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', borderRadius: '10px', border: '1.5px solid #e2e8f0', background: 'white', color: '#173b78', cursor: 'pointer', flexShrink: 0 }}>
            <ArrowLeft size={16} />
          </button>
        )}
        <h1 className="ui-title" style={{ margin: 0 }}>Expédition</h1>
      </div>
      <p className="ui-subtitle" style={{ marginBottom: '18px' }}>Livraisons hors Dakar · tarif propre à chaque boutique</p>

      {/* Stats */}
      <div className="stat-tiles stat-tiles--3">
        {[
          { label: 'En transit', value: transit.length,   accent: '#173b78', img: '/images/stat-en-transit.webp',          alt: 'Colis en cours de route' },
          { label: 'Livrées',    value: delivered.length, accent: '#16a34a', img: '/images/stat-commandes-confirmees.webp', alt: 'Colis livré au client' },
          { label: 'Retours',    alert: true, value: failed.length,    accent: '#c53a2d', img: '/images/stat-commandes-retours.webp',    alt: 'Colis retourné' },
        ].map((s) => (
          <div key={s.label} className="stat-tile stat-tile--static">
            <span className={`stat-tile-bar${s.alert ? ' stat-tile-bar--alert' : ''}`} />
            <img className="stat-tile-img" src={s.img} alt={s.alt} loading="lazy" width="44" height="44" />
            <span className="stat-tile-text">
              <span className="stat-tile-value" style={{ color: s.accent }}>{loading ? '—' : s.value}</span>
              <span className="stat-tile-label">{s.label}</span>
            </span>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="tab-strip" style={{ marginBottom: '14px' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`tab-btn${tab === t.id ? ' active' : ''}`}>
            {t.label}
            {t.count > 0 && (
              <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '20px', background: tab === t.id ? '#173b78' : '#cbd5e1', color: 'white', fontWeight: 800 }}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── EN TRANSIT ─────────────────────────────────────────────── */}
      {tab === 'transit' && (
        loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[0,1,2].map(i => <SkeletonRow key={i} />)}
          </div>
        ) : transit.length === 0 ? (
          <EmptyState icon={Truck} label="Aucune expédition en cours" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {groupByDay(transit, 'expedition_at').map(group => (
              <DayGroup key={group.key} group={group} onOrderClick={o => setSelected(o)} />
            ))}
          </div>
        )
      )}

      {/* ── LIVRÉES ────────────────────────────────────────────────── */}
      {tab === 'delivered' && (
        loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[0,1,2].map(i => <SkeletonRow key={i} />)}
          </div>
        ) : (
          <>
            {pendingDelivered.length > 0 && (
              <div style={{ borderRadius: '16px', overflow: 'hidden', marginBottom: '16px', boxShadow: '0 6px 20px rgba(10,22,40,0.15)' }}>
                <div style={{ background: 'linear-gradient(135deg, #07162d 0%, #173b78 100%)', padding: '16px 18px' }}>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '1.4px', fontWeight: 700 }}>À collecter chez le collaborateur</div>
                  <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '32px', fontWeight: 800, color: 'white', lineHeight: 1.1, marginTop: '4px' }}>
                    {fmt(totalToCollect)} <span style={{ fontSize: '13px', opacity: 0.55 }}>FCFA</span>
                  </div>
                </div>
                <div style={{ background: 'rgba(19,40,72,0.05)', borderTop: '1px solid rgba(23,59,120,0.1)', padding: '10px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                    {pendingDelivered.length} livraison{pendingDelivered.length > 1 ? 's' : ''} non collectée{pendingDelivered.length > 1 ? 's' : ''}
                  </span>
                  <button onClick={collecterTout} disabled={collecting}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', borderRadius: '10px', border: 'none', background: collecting ? '#e2e8f0' : 'linear-gradient(135deg, #173b78, #2a66b8)', color: collecting ? '#94a3b8' : 'white', fontSize: '12px', fontWeight: 700, cursor: collecting ? 'not-allowed' : 'pointer', boxShadow: collecting ? 'none' : '0 2px 8px rgba(23,59,120,0.3)' }}>
                    <Wallet size={13} />
                    {collecting ? 'En cours...' : `Collecter ${fmt(totalToCollect)} F`}
                  </button>
                </div>
              </div>
            )}

            {pendingDelivered.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                {groupByDay(pendingDelivered, 'delivered_at').map(group => (
                  <DayGroup key={group.key} group={group} onOrderClick={o => setSelected(o)} />
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderRadius: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '20px' }}>
                <CheckCircle2 size={18} color="#15803d" />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#15803d' }}>Tout a été collecté</div>
                  {delivered.length > 0 && <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '2px' }}>{delivered.length} livraison{delivered.length > 1 ? 's' : ''} dans l'historique</div>}
                </div>
              </div>
            )}

            {collectionHistory.length > 0 && (
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>Historique des collectes</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {collectionHistory.map(v => {
                    const isOpen = expandedVersement === v.id;
                    return (
                      <div key={v.id} style={{ borderRadius: '12px', border: '1px solid #dcfce7', overflow: 'hidden', background: 'white' }}>
                        <div onClick={() => setExpandedVersement(isOpen ? null : v.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', cursor: 'pointer', background: isOpen ? '#f0fdf4' : 'white' }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '9px', background: 'linear-gradient(135deg, #15803d, #166534)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <CheckCircle2 size={14} color="white" />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>{dayLabel(v.created_at)}</div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>
                              {v.nb_deliveries} livraison{v.nb_deliveries > 1 ? 's' : ''} · {formatTime(v.created_at)}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '15px', fontWeight: 900, color: '#15803d' }}>{fmt(v.amount)} F</div>
                          </div>
                          {isOpen ? <ChevronUp size={14} color="#94a3b8" /> : <ChevronDown size={14} color="#94a3b8" />}
                        </div>
                        {isOpen && (
                          <div style={{ borderTop: '1px solid #f0fdf4', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            {v.items.length > 0 ? v.items.map(o => (
                              <div key={o.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '8px 10px', borderRadius: '9px', background: '#f8fafc' }}>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {o.client}{o.order_number && <span style={{ marginLeft: '5px', fontSize: '10px', color: '#94a3b8' }}>#{o.order_number}</span>}
                                  </div>
                                  {o.address && <div style={{ fontSize: '10px', color: '#64748b', marginTop: '1px' }}>{o.address}</div>}
                                </div>
                                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                  <div style={{ fontSize: '12px', fontWeight: 800, color: '#173b78' }}>{fmt(o.amount)} F</div>
                                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>{formatTime(o.delivered_at)}</div>
                                </div>
                              </div>
                            )) : (
                              <div style={{ padding: '10px', textAlign: 'center', fontSize: '12px', color: '#94a3b8' }}>Détails non disponibles</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )
      )}

      {/* ── RETOURS ────────────────────────────────────────────────── */}
      {tab === 'failed' && (
        loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[0,1,2].map(i => <SkeletonRow key={i} />)}
          </div>
        ) : failed.length === 0 ? (
          <EmptyState icon={RotateCcw} label="Aucun retour enregistré" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {groupByDay(failed, 'delivered_at').map(group => (
              <DayGroup key={group.key} group={group} onOrderClick={o => setSelected(o)} />
            ))}
          </div>
        )
      )}

      </div>

      {/* ── MODAL MOTIF RETOUR ─────────────────────────────────────── */}
      {motifModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1005, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(10,18,40,0.7)', backdropFilter: 'blur(6px)' }}
          onClick={() => setMotifModal(null)}>
          <div style={{ background: 'white', borderRadius: '24px 24px 0 0', width: 'min(640px, 100vw)', padding: '24px 20px', paddingBottom: 'max(24px, env(safe-area-inset-bottom, 24px))', boxShadow: '0 -24px 80px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#e2e8f0', margin: '0 auto 20px' }} />
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Motif du retour</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>Pourquoi cette expédition n'a pas pu être livrée ?</div>
            <textarea
              autoFocus
              value={motifText}
              onChange={e => setMotifText(e.target.value)}
              placeholder="Ex: Destinataire absent, adresse introuvable, colis refusé..."
              rows={3}
              style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #e2e8f0', borderRadius: '12px', fontSize: '14px', fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box', color: '#0f172a' }}
              onFocus={e => { e.target.style.borderColor = '#be123c'; }}
              onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
              <button onClick={() => setMotifModal(null)}
                style={{ flex: 1, padding: '14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: 'white', fontSize: '14px', fontWeight: 700, color: '#64748b', cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={handleMotifConfirm} disabled={saving}
                style={{ flex: 2, padding: '14px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #f43f5e, #be123c)', fontSize: '14px', fontWeight: 800, color: 'white', cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 6px 16px rgba(190,18,60,0.3)' }}>
                {saving ? 'Enregistrement...' : 'Confirmer le retour'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── BOTTOM SHEET ───────────────────────────────────────────── */}
      {selected && (() => {
        const s = statusCfg[selected.status] || statusCfg.transit;
        const digits = String(selected.phone || '').replace(/\D/g, '');
        const waNum = digits.length === 9 ? `221${digits}` : digits;
        const isTransit = ['pending', 'transit'].includes(selected.status);
        return (
          <>
            {/* Backdrop — au-dessus de tout (bottom nav à 999, sidebar à 1002) */}
            <div style={{ position: 'fixed', inset: 0, zIndex: 1003, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }} onClick={closeSheet} />
            <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 'min(640px, 100vw)', zIndex: 1004, background: 'white', borderRadius: '22px 22px 0 0', boxShadow: '0 -8px 40px rgba(0,0,0,0.18)', paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))' }}>

              {/* Drag handle */}
              <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 2px' }}>
                <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: '#e2e8f0' }} />
              </div>

              <div style={{ padding: '0 16px 12px', display: 'flex', flexDirection: 'column', gap: '7px' }}>

                {/* ── En-tête : nom + montant + contact + fermer ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* badges */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                    {selected.order_number && (
                      <span style={{ fontSize: '10px', fontWeight: 800, color: '#173b78', fontFamily: 'monospace', background: '#eff6ff', padding: '2px 7px', borderRadius: '5px' }}>#{selected.order_number}</span>
                    )}
                    <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '6px', background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{s.label}</span>
                  </div>
                  <div style={{ flex: 1 }} />
                  {/* contact icons */}
                  {selected.phone && (
                    <>
                      <a href={`tel:${selected.phone}`} style={{ width: '34px', height: '34px', borderRadius: '10px', background: '#eff6ff', border: '1.5px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', flexShrink: 0 }}>
                        <Phone size={14} color="#1d4ed8" />
                      </a>
                      <a href={`https://wa.me/${waNum}`} target="_blank" rel="noopener noreferrer" style={{ width: '34px', height: '34px', borderRadius: '10px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', flexShrink: 0 }}>
                        <MessageCircle size={14} color="#15803d" />
                      </a>
                    </>
                  )}
                  <button onClick={closeSheet}
                    style={{ width: '34px', height: '34px', borderRadius: '10px', border: '1.5px solid #e5e7eb', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <X size={14} color="#64748b" />
                  </button>
                </div>

                {/* Nom + montant */}
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', fontFamily: 'Alan Sans, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{selected.client}</div>
                  {selected.amount && (
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <span style={{ fontSize: '17px', fontWeight: 900, color: '#173b78', fontFamily: 'Alan Sans, sans-serif' }}>{fmt(selected.amount)}</span>
                      <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600, marginLeft: '3px' }}>FCFA</span>
                    </div>
                  )}
                </div>

                {/* ── Infos ── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>

                  {/* Produit */}
                  {selected.product_name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f5f3ff', borderRadius: '10px', border: '1px solid #ddd6fe', padding: '8px 12px' }}>
                      <Package size={13} color="#6366f1" style={{ flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: '13px', fontWeight: 700, color: '#3730a3' }}>
                        {selected.product_name}{selected.quantity > 1 ? ` × ${selected.quantity}` : ''}
                      </span>
                    </div>
                  )}

                  {/* Boutique */}
                  {selected.store && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '8px 12px' }}>
                      <Store size={12} color="#94a3b8" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{selected.store}</span>
                    </div>
                  )}

                  {/* Adresse */}
                  {selected.address && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '8px 12px' }}>
                      <MapPin size={12} color="#94a3b8" style={{ flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: '12px', fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.address}</span>
                      <a href={`https://maps.google.com/?q=${encodeURIComponent(selected.address)}`} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: '3px', padding: '4px 9px', borderRadius: '7px', border: '1px solid #dbeafe', background: '#eff6ff', color: '#1d4ed8', fontSize: '10px', fontWeight: 700, textDecoration: 'none', flexShrink: 0 }}>
                        <Navigation size={9} /> Maps
                      </a>
                    </div>
                  )}

                  {/* Date expédition */}
                  {selected.expedition_at && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '8px 12px' }}>
                      <Calendar size={12} color="#94a3b8" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                        Expédié le {new Date(selected.expedition_at).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  )}
                </div>

                {/* ── Motif retour ── */}
                {selected.status === 'failed' && selected.return_reason && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', padding: '8px 12px' }}>
                    <AlertTriangle size={13} color="#c2410c" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#7c2d12', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.return_reason}</span>
                  </div>
                )}

                {/* ── ACTIONS ── */}

                {/* Transit → Livré + Retour + Dakar */}
                {isTransit && (
                  <>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => markStatus(selected.id, 'delivered', selected.client)} disabled={saving}
                        style={{ flex: 1, padding: '12px 8px', borderRadius: '11px', border: 'none', background: saving ? '#e2e8f0' : 'linear-gradient(135deg, #22c55e, #15803d)', color: saving ? '#94a3b8' : 'white', fontSize: '13px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <CheckCircle size={14} /> Marquer livré
                      </button>
                      <button onClick={handleRetourClick} disabled={saving}
                        style={{ flex: 1, padding: '12px 8px', borderRadius: '11px', border: 'none', background: saving ? '#e2e8f0' : 'linear-gradient(135deg, #f87171, #be123c)', color: saving ? '#94a3b8' : 'white', fontSize: '13px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <XCircle size={14} /> Retour
                      </button>
                    </div>
                    <button onClick={() => setShowDakarPicker(v => !v)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderRadius: '11px', border: `1.5px solid ${showDakarPicker ? '#bfdbfe' : '#e2e8f0'}`, background: showDakarPicker ? '#eff6ff' : '#f8fafc', cursor: 'pointer', fontSize: '13px', fontWeight: 700, color: '#173b78' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                        <ArrowLeft size={13} color="#173b78" /> Ramener dans les livraisons Dakar
                      </div>
                      <ChevronDown size={13} color="#94a3b8" style={{ transform: showDakarPicker ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                    </button>
                    {showDakarPicker && <DakarPicker drivers={drivers} saving={saving} onPick={ramenerDakar} />}
                  </>
                )}

                {/* Delivered non collecté → Marquer retour */}
                {selected.status === 'delivered' && !selected.expedition_versement_id && (
                  <button onClick={handleRetourClick} disabled={saving}
                    style={{ width: '100%', padding: '12px', borderRadius: '11px', border: '1.5px solid #fecdd3', background: '#fff1f2', color: '#be123c', fontSize: '13px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}>
                    <RotateCcw size={14} /> Marquer comme retour
                  </button>
                )}

                {/* Failed → Réexpédier + Dakar + Annuler */}
                {selected.status === 'failed' && (
                  <>
                    <button onClick={reexpedier} disabled={saving}
                      style={{ width: '100%', padding: '12px', borderRadius: '11px', border: 'none', background: saving ? '#e2e8f0' : 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: saving ? '#94a3b8' : 'white', fontSize: '13px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}>
                      <RotateCcw size={14} /> Réexpédier
                    </button>
                    <button onClick={() => setShowDakarPicker(v => !v)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderRadius: '11px', border: `1.5px solid ${showDakarPicker ? '#bfdbfe' : '#e2e8f0'}`, background: showDakarPicker ? '#eff6ff' : '#f8fafc', cursor: 'pointer', fontSize: '13px', fontWeight: 700, color: '#173b78' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                        <ArrowLeft size={13} color="#173b78" /> Ramener dans les livraisons Dakar
                      </div>
                      <ChevronDown size={13} color="#94a3b8" style={{ transform: showDakarPicker ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                    </button>
                    {showDakarPicker && <DakarPicker drivers={drivers} saving={saving} onPick={ramenerDakar} />}
                    <button onClick={() => setConfirmCancel({ id: selected.id, name: selected.client })}
                      style={{ width: '100%', padding: '11px', borderRadius: '11px', border: '1.5px solid #fecdd3', background: 'white', color: '#be123c', fontSize: '13px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}>
                      <XCircle size={14} /> Annuler définitivement
                    </button>
                  </>
                )}

              </div>
            </div>
          </>
        );
      })()}

      <ConfirmDialog
        isOpen={!!confirmCancel}
        title="Annuler définitivement"
        message={`Annuler la commande de ${confirmCancel?.name} ? Cette action est irréversible.`}
        confirmLabel="Annuler la commande"
        onConfirm={() => cancelOrder(confirmCancel.id)}
        onCancel={() => setConfirmCancel(null)}
      />
    </div>
  );
}

function DakarPicker({ drivers, saving, onPick }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '2px 0' }}>
      <p style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, margin: '0 0 2px 2px' }}>
        La commande quittera l'expédition et reviendra dans le flux Dakar.
      </p>
      <button onClick={() => onPick(null)} disabled={saving}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '11px', border: '1.5px solid #e2e8f0', background: 'white', cursor: saving ? 'not-allowed' : 'pointer', textAlign: 'left' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '9px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <User size={14} color="#64748b" />
        </div>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#374151' }}>Sans livreur</div>
          <div style={{ fontSize: '11px', color: '#94a3b8' }}>À assigner manuellement</div>
        </div>
      </button>
      {drivers.map(driver => (
        <button key={driver.id} onClick={() => onPick(driver.id)} disabled={saving}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '11px', border: '1.5px solid #e2e8f0', background: 'white', cursor: saving ? 'not-allowed' : 'pointer', textAlign: 'left' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '9px', background: AVATAR_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', color: AVATAR_FG, fontWeight: 800, fontSize: '12px', flexShrink: 0 }}>
            {driver.initials}
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{driver.name}</div>
            <div style={{ fontSize: '11px', color: driver.status === 'available' ? '#16a34a' : '#94a3b8' }}>
              {driver.status === 'available' ? 'Disponible' : driver.status === 'busy' ? 'En tournée' : 'Hors ligne'}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function EmptyState({ icon: Icon, label }) {
  return (
    <div style={{ background: 'white', borderRadius: '16px', padding: '56px 24px', textAlign: 'center', border: '1px solid #eef0f6' }}>
      <Icon size={32} color="#e2e8f0" style={{ margin: '0 auto 12px' }} />
      <div style={{ fontSize: '14px', fontWeight: 700, color: '#94a3b8' }}>{label}</div>
    </div>
  );
}
